<template>
  <sp-menu
    v-if="i18nReady"
    class="distance-filter"
    :open="menuOpen"
    :disabled="disabled"
    :permanent="permanent"
    :close-on-content-click="closeOnContentClick"
    location="bottom-left"
    @update-open="handleUpdateOpen"
  >
    <div slot="activator">
      <slot name="activator" :label="activatorLabel" :text="activatorTitle" :prepend-icon="prependIcon" :open="open">
        <DefaultFilterActivator
          :value="activatorTitle"
          :label="activatorLabel"
          :prepend-icon="prependIcon"
          no-divider
          class="distance-filter__activator"
          :non-clearable="isNonClearable"
          :disabled="disabled"
          @clear="clearSelectedModel"
        />
      </slot>
    </div>

    <sp-card>
      <sp-card-content>
        <div class="distance-filter__header">
          <sp-text type="body-bold" class="distance-filter__title">{{ dropdownTitleText }}</sp-text>
          <sp-button
            v-if="geolocationSupported"
            variant="text"
            size="small"
            prepend-icon="gps-locate-filled"
            :loading="isRetrievingCurrentLocation"
            :disabled="currentLocationDisabled"
            class="distance-filter__current-location"
            :data-testid="currentLocationTestId"
            @click="handleCurrentLocation"
          >
            {{ currentLocationButtonLabel }}
          </sp-button>
        </div>

        <sp-google-maps-auto-complete
          v-preserve-properties:bounds="searchBoundsValue"
          v-preserve-properties:sort-order="placeSortOrder"
          :disabled="disabled"
          input-density="compact"
          :input-test-id="TestId.finder.distanceFilterInput"
          :placeholder="searchPlaceholderText"
          :search-query="searchQuery"
          @update-model-value="handleGoogleMapsResultChange"
          @update-search-query="handleSearchQueryChange"
        ></sp-google-maps-auto-complete>

        <!-- The commit channel, not v-model: `input` streams every drag tick, and this consumer
             fetches per update. `.prop` carries programmatic updates the attribute channel would
             treat as defaults. -->
        <sp-number-slider
          v-if="showDistanceSlider"
          v-preserve-properties:values="distanceValues"
          :disabled="disabled"
          class="distance-filter__distance-slider"
          :label="t('distance_maximum')"
          :value.prop="selectedDistance"
          :input-test-id="TestId.finder.distanceSliderInput"
          @change="handleDistanceInput"
        ></sp-number-slider>
        <sp-select
          v-else-if="selectedLocation"
          v-model="distanceSelection"
          v-preserve-properties:items="distanceOptions"
          :disabled="disabled"
          :prompt="anyDistancePrompt"
          :label="t('distance_label')"
          density="compact"
          class="distance-filter__distance-select"
          :prepend-selected-with="selectedDistancePrependText"
          @input="handleDistanceInput"
        ></sp-select>
      </sp-card-content>

      <sp-fieldset v-if="showLocationChoices" class="distance-filter__distance-list">
        <sp-quantum-select
          v-preserve-properties:items="locations"
          :disabled="disabled"
          :aria-label="activatorLabel"
          :clearable="!isNonClearable"
          no-filter
          no-menu
          hide-title-bar
          hide-search-bar
          mobile-expand-height-max
          :listen-on-key-event="menuOpen"
          :item-test-id="TestId.finder.distanceFilterOption"
          item-title="name"
          :loading="loading"
          :pointer="!hidePointer"
          @update-selected="handleUpdateSelectedLocation"
        >
        </sp-quantum-select>
      </sp-fieldset>
    </sp-card>
  </sp-menu>
</template>

<script setup>
import { vPreserveProperties } from "#ce/utils/property-bindings";
import { watchImmediate } from "@vueuse/core";
import { computed, onMounted, ref, watch } from "vue";
import { useForm } from "#ce/composables/useForm";
import { toBoolean, toJSON, trimToUndefined } from "#ce/utils/props";
import { TestId } from "#/test-ids";
import { useDistanceFilter } from "#ce/shared/filters/distance/composables/useDistanceFilter";
import { t, i18nReady } from "#ce/shared/filters/i18n";
import DefaultFilterActivator from "#ce/shared/filters/components/DefaultFilterActivator.ce.vue";
import { props as distanceFilterProps } from "./props.js";

const { mirrorFormValue } = useForm();

const emit = defineEmits(["update:open", "change", "update:model-value"]);

const props = defineProps(distanceFilterProps);

const distanceFilter = useDistanceFilter(props);
const {
  displayText,
  distanceOptions,
  anyDistancePrompt,
  selectedDistancePrependText,
  model,
  searchQuery,
  loading,
  locations,
  selectedDistance,
  pickedDistance,
  selectedLocation,
  sliderControl,
  distances: distanceValues,
  placeSortOrder,
  filterNames,
  applyingSeed,
  transientLocation,
  geolocationSupported,
  isRetrievingCurrentLocation,
  selectCurrentLocation,
} = distanceFilter;

const currentLocationDisabled = computed(() => toBoolean(props.disabled) || isRetrievingCurrentLocation.value);
const choosingLocation = ref(false);
watch(selectedLocation, finishChoosingLocation);

function finishChoosingLocation() {
  choosingLocation.value = false;
}
const showLocationChoices = computed(() => !selectedLocation.value || choosingLocation.value);
const menuOpen = ref(toBoolean(props.open));
const isNonClearable = computed(() => toBoolean(props.nonClearable));
const showDistanceSlider = computed(() => Boolean(selectedLocation.value) && sliderControl.value);

function syncMenuOpen(value) {
  menuOpen.value = toBoolean(value);
}

function reportMenuOpen(value) {
  emit("update:open", value);
}

watch(() => props.open, syncMenuOpen);
watch(menuOpen, reportMenuOpen);

/**
 * Props model value is generally ignored, it acts as a compatibility layer for the filter-bar.
 * When a user clears the filter from the filter-bar, the model value is set to `undefined`. which should clear the
 * distance filter.
 */
function clearOnModelReset(value) {
  if (!value) {
    distanceFilter.clear();
  }
}

let locationSelected = false;
watch(() => props.modelValue, clearOnModelReset);
watchImmediate(model, handleModelChange);

function handleModelChange(value) {
  const metadata = { seeded: applyingSeed.value };
  if (selectedLocation.value) {
    metadata.locationDisplayName = selectedLocation.value.name;
  }
  if (transientLocation.value) {
    metadata.transientLocation = true;
  }
  if (locationSelected) {
    metadata.locationSelected = true;
  }
  emit("update:model-value", value, metadata);
  locationSelected = false;
}

// The host app dispatches the one change per user update.
mirrorFormValue(model);

async function initializeDistanceFilter() {
  await distanceFilter.initialize();
}

onMounted(initializeDistanceFilter);

function handleUpdateSelectedLocation({ detail }) {
  if (toBoolean(props.disabled)) {
    return;
  }
  const [selected] = detail;
  const [location] = selected;
  choosingLocation.value = false;
  locationSelected = true;
  distanceFilter.updateSelectedLocation(location);
  conditionallyCloseMenu();
}

function handleUpdateOpen({ detail }) {
  if (toBoolean(props.disabled)) {
    return;
  }
  const [isOpen] = detail;
  menuOpen.value = isOpen;
}

function handleCurrentLocation() {
  if (toBoolean(props.disabled)) {
    return;
  }
  selectCurrentLocation();
}

function handleDistanceInput({ detail }) {
  if (toBoolean(props.disabled)) {
    return;
  }
  const [value] = detail;
  selectedDistance.value = value;
}

function handleGoogleMapsResultChange({ detail }) {
  if (toBoolean(props.disabled)) {
    return;
  }
  const [result] = detail;
  distanceFilter.updateGoogleResult(result);
}

function handleSearchQueryChange({ detail }) {
  if (toBoolean(props.disabled)) {
    return;
  }
  const [query] = detail;
  choosingLocation.value = true;
  distanceFilter.handleSearchInput(query);
}

function conditionallyCloseMenu() {
  if (toBoolean(props.closeOnSelect)) {
    menuOpen.value = false;
  }
}

const activatorTitle = computed(() => displayText.value);

const activatorLabel = computed(() => trimToUndefined(props.label) ?? t("distance_activator_label"));
const dropdownTitleText = computed(() => trimToUndefined(props.dropdownTitle) ?? t("distance_title"));
const searchPlaceholderText = computed(() => trimToUndefined(props.searchPlaceholder) ?? t("distance_search"));
const searchBoundsValue = computed(() => toJSON(trimToUndefined(props.searchBounds), true));

const currentLocationButtonLabel = computed(() =>
  isRetrievingCurrentLocation.value
    ? (trimToUndefined(props.currentLocationRetrievingLabel) ?? t("current_location_retrieving"))
    : (trimToUndefined(props.currentLocationLabel) ?? t("current_location")),
);

const active = computed(() => Boolean(selectedLocation.value));

const distanceSelection = computed({
  get: () => pickedDistance.value,
  set: distanceFilter.selectDistance,
});

function clearSelectedModel() {
  if (toBoolean(props.disabled)) {
    return;
  }
  if (isNonClearable.value) {
    return;
  }
  distanceFilter.clear();
  emit("change", undefined);
}

function clear() {
  if (isNonClearable.value) {
    return;
  }
  distanceFilter.clear();
}

defineExpose({
  props,
  filterNames,
  setFilterValues: distanceFilter.setFilterValues,
  clear,
  active,
  selectedText: displayText,
});
</script>

<style scoped lang="scss">
@import "@assets/mixins/breakpoint";

.distance-filter {
  --sp-comp-popup-min-height: auto;
  --sp-comp-popup-width: var(--sp-comp-filter-bar-popup-width, 20rem);
  --sp-comp-popup-content-overflow: visible;
  --sp-comp-card-overflow: visible;
  --sp-comp-text-field-color: var(--sp-sys-color-on-surface);

  --sp-comp-search-box-glass-icon-fill: var(--sp-sys-color-outline);
  --sp-comp-search-box-clear-icon-fill: var(--sp-sys-color-outline);

  --sp-comp-list-item-icon-size: var(--sp-comp-distance-filter-list-item-icon-size, 2rem);
  --sp-comp-list-item-align-items: var(--sp-comp-distance-filter-list-item-align-items, start);

  --sp-comp-list-item-padding-inline: var(--sp-comp-distance-filter-list-item-padding-inline, 0.75rem);
  --sp-comp-list-item-prepend-margin-left: var(
    --sp-comp-distance-filter-list-item-prepend-margin-left,
    var(--sp-comp-list-item-padding-inline)
  );
}

.distance-filter sp-select {
  --sp-comp-text-color: var(--sp-comp-distance-filter-distance-label-color, inherit);
  --sp-comp-select-label-font: var(--sp-comp-distance-filter-distance-label-font, var(--sp-sys-typescale-body-short));
}

.distance-filter__header {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: var(--sp-ref-scale-6);
  margin-bottom: var(--sp-ref-scale-8);
}

// Font-family override hooks for the two header labels. The `*-short` typescale
// tokens bake the family at :root, so a family-only override can't reach them —
// we re-declare each element's shorthand here from its sub-tokens and expose just
// the family as a public `--sp-comp-distance-filter-*-font-family` token. Both
// default to the element's normal family, so consumers that don't set them render
// unchanged.
.distance-filter__title {
  --sp-sys-typescale-body-bold-short: var(--sp-sys-typescale-body-bold-font-weight)
    var(--sp-sys-typescale-body-font-size) / var(--sp-sys-typescale-body-bold-line-height)
    var(--sp-comp-distance-filter-title-font-family, var(--sp-sys-typescale-body-bold-font-family));
}

.distance-filter__current-location {
  justify-self: end;
  --sp-comp-button-small-font-short: var(--sp-sys-typescale-small-bold-font-weight)
    var(--sp-sys-typescale-small-font-size) / var(--sp-sys-typescale-small-line-height)
    var(--sp-comp-distance-filter-current-location-font-family, var(--sp-sys-typescale-small-bold-font-family));
}

.distance-filter__distance-select {
  margin-top: calc(var(--sp-ref-scale-12) * 2);
}

.distance-filter__distance-slider {
  margin-top: calc(var(--sp-ref-scale-12) * 2);
  // Match the "Near" dropdown title above — the same shorthand
  // .distance-filter__title composes, including its font-family hook.
  --sp-comp-number-slider-label-font: var(--sp-sys-typescale-body-bold-font-weight)
    var(--sp-sys-typescale-body-font-size) / var(--sp-sys-typescale-body-bold-line-height)
    var(--sp-comp-distance-filter-title-font-family, var(--sp-sys-typescale-body-bold-font-family));
}

sp-card-content {
  padding-top: var(--sp-ref-scale-12);
  padding-bottom: var(--sp-ref-scale-20);
  padding-inline: var(--sp-comp-distance-filter-padding-inline, var(--sp-ref-scale-12));
}

.distance-filter__activator {
  @include sp-breakpoint(sm) {
    --activator-border-width: var(--sp-comp-distance-filter-desktop-border-width, 0);
  }
}
</style>
